import { ClientLambdaApi } from '@/api-client.js'

export const newsFeedList = (page, search = '', perPage = 20, filters = {}) => {
  const params = new URLSearchParams({ page, per_page: perPage, include_check_ins: true })
  if (search) params.append('search', search)
  if (filters.hashtag_id) params.append('hashtag_id', filters.hashtag_id)
  if (filters.from_date) params.append('from_date', filters.from_date)
  if (filters.to_date) params.append('to_date', filters.to_date)
  return ClientLambdaApi.get(`/news-feed-list?${params.toString()}`)
    .then(response => {
      if (response.status !== 200) throw new Error('Error al obtener el listado del feed')
      return response.data
    })
}

export const newsFeedMetrics = () => {
  return ClientLambdaApi.get('/news-feed/news-feed-metrics')
    .then(response => {
      if (response.status !== 200) throw new Error('Error al obtener las métricas del feed')
      return response.data
    })
}

export const postNewContentToFeed = async (payload) => {
  const response = await ClientLambdaApi.post('/post-new-content-to-feed/', payload, {
    needTokenValidation: true
  })
  if (response.data && response.data.status === 'ERROR') {
    throw new Error(response.data.description || 'Error al subir el contenido al feed')
  }
  if (response.status !== 201) throw new Error('Error al subir el contenido al feed')
  return response.data
}

export const getHashtags = (search = '') => {
  const params = search ? `?search=${encodeURIComponent(search)}` : ''
  return ClientLambdaApi.get(`/news-feed/hashtags${params}`)
    .then(response => {
      if (response.status !== 200) throw new Error('Error al obtener los hashtags')
      return response.data
    })
}

export const addPostHashtags = (postId, hashtagIds) => {
  return ClientLambdaApi.post(`/news-feed/${postId}/hashtags`, { hashtag_ids: hashtagIds })
    .then(response => {
      if (response.status !== 200 && response.status !== 201) throw new Error('Error al agregar hashtags')
      return response.data
    })
}

export const removePostHashtag = (postId, hashtagId) => {
  return ClientLambdaApi.delete(`/news-feed/${postId}/hashtags/${hashtagId}`)
    .then(response => {
      if (response.status !== 200 && response.status !== 204) throw new Error('Error al quitar el hashtag')
      return response.data
    })
}

export const getPostComments = (postId, page = 1, perPage = 20) => {
  return ClientLambdaApi.get(`/news-feed/${postId}/comments?page=${page}&per_page=${perPage}`)
    .then(response => {
      if (response.status !== 200) throw new Error('Error al obtener los comentarios')
      return response.data
    })
}

export const getMentionableUsers = (search = '') => {
  const params = search ? `?search=${encodeURIComponent(search)}` : ''
  return ClientLambdaApi.get(`/news-feed/mentionable-users${params}`)
    .then(response => {
      if (response.status !== 200) throw new Error('Error al obtener usuarios mencionables')
      return response.data
    })
}

export const createComment = (postId, content, mentionedUserIds = []) => {
  const body = { content }
  if (mentionedUserIds.length > 0) body.mentioned_user_ids = mentionedUserIds
  return ClientLambdaApi.post(`/news-feed/${postId}/comments`, body)
    .then(response => {
      if (response.status !== 201) throw new Error('Error al crear el comentario')
      return response.data
    })
}

export const updateComment = (commentId, content, mentionedUserIds = undefined) => {
  const body = { content }
  if (mentionedUserIds !== undefined) body.mentioned_user_ids = mentionedUserIds
  return ClientLambdaApi.put(`/news-feed/comments/${commentId}`, body)
    .then(response => {
      if (response.status !== 200) throw new Error('Error al editar el comentario')
      return response.data
    })
}

export const deleteComment = (commentId) => {
  return ClientLambdaApi.delete(`/news-feed/comments/${commentId}`)
    .then(response => {
      if (response.status !== 200) throw new Error('Error al eliminar el comentario')
      return response.data
    })
}

export const deletePost = async (postId) => {
  const response = await ClientLambdaApi.delete(`/news-feed/${postId}`)
  if (response.status === 403) throw new Error('No tienes permiso para eliminar esta publicación')
  if (response.status === 404) throw new Error('La publicación no existe o ya fue eliminada')
  if (response.status !== 200) throw new Error('Error al eliminar la publicación')
  return response.data
}

export const deleteCheckInOut = async (checkInId, target) => {
  const response = await ClientLambdaApi.delete(`/user-store-check-in-out/${checkInId}?target=${target}`)
  if (response.status === 400) throw new Error('Parámetro target inválido o ausente')
  if (response.status === 401) throw new Error('No autorizado')
  if (response.status !== 200) throw new Error('Error al eliminar el registro')
  return response.data
}

export const getHashtagDashboard = (startDate, endDate) => {
  const params = new URLSearchParams()
  if (startDate) params.append('start_date', startDate)
  if (endDate) params.append('end_date', endDate)
  const query = params.toString()
  return ClientLambdaApi.get(`/news-feed/hashtags/dashboard${query ? '?' + query : ''}`)
    .then(response => {
      if (response.status !== 200) throw new Error('Error al obtener el dashboard de hashtags')
      return response.data
    })
}

export const getExecutionFocusRuleTypes = () => {
  return ClientLambdaApi.get('/execution-focus/rule-types')
    .then(response => {
      if (response.status !== 200) throw new Error('Error al obtener los tipos de verificación')
      return response.data
    })
}

export const createExecutionFocusRuleType = (payload) => {
  return ClientLambdaApi.post('/execution-focus/rule-types', payload)
    .then(response => {
      if (response.status !== 201) throw new Error('Error al crear el tipo de verificación')
      return response.data
    })
}

export const updateExecutionFocusRuleType = (id, payload) => {
  return ClientLambdaApi.put(`/execution-focus/rule-types/${id}`, payload)
    .then(response => {
      if (response.status !== 200) throw new Error('Error al actualizar el tipo de verificación')
      return response.data
    })
}

export const deleteExecutionFocusRuleType = (id) => {
  return ClientLambdaApi.delete(`/execution-focus/rule-types/${id}`)
    .then(response => {
      if (response.status !== 200) throw new Error('Error al desactivar el tipo de verificación')
      return response.data
    })
}

export const getExecutionFocusList = (filters = {}) => {
  const params = new URLSearchParams()
  if (filters.active !== undefined && filters.active !== null) params.append('active', filters.active)
  if (filters.start_date) params.append('start_date', filters.start_date)
  if (filters.end_date) params.append('end_date', filters.end_date)
  const query = params.toString()
  return ClientLambdaApi.get(`/execution-focus${query ? '?' + query : ''}`)
    .then(response => {
      if (response.status !== 200) throw new Error('Error al obtener los focos de ejecución')
      return response.data
    })
}

export const getExecutionFocus = (focusId) => {
  return ClientLambdaApi.get(`/execution-focus/${focusId}`)
    .then(response => {
      if (response.status !== 200) throw new Error('Error al obtener el foco de ejecución')
      return response.data
    })
}

export const createExecutionFocus = (payload) => {
  return ClientLambdaApi.post('/execution-focus', payload)
    .then(response => {
      if (response.status !== 201) throw new Error('Error al crear el foco de ejecución')
      return response.data
    })
}

export const updateExecutionFocus = (focusId, payload) => {
  return ClientLambdaApi.put(`/execution-focus/${focusId}`, payload)
    .then(response => {
      if (response.status !== 200) throw new Error('Error al actualizar el foco de ejecución')
      return response.data
    })
}

export const deactivateExecutionFocus = async (focusId) => {
  try {
    const response = await ClientLambdaApi.patch(`/execution-focus/${focusId}/deactivate`, {})
    if (response.status !== 200 && response.status !== 204) {
      throw new Error('Error al desactivar el foco de ejecución')
    }
    return response.data
  } catch (err) {
    const status = err?.status
    if (status === 404) throw new Error('No se encontró el foco de ejecución.')
    const msg = err?.data?.message
    if (typeof msg === 'string' && msg.trim()) throw new Error(msg)
    if (err instanceof Error && err.message) throw err
    throw new Error('Error al desactivar el foco de ejecución')
  }
}

export const deleteExecutionFocus = async (focusId) => {
  try {
    const response = await ClientLambdaApi.delete(`/execution-focus/${focusId}`)
    if (response.status !== 200) throw new Error('Error al eliminar el foco de ejecución')
    return response.data
  } catch (err) {
    const status = err?.status
    if (status === 404) throw new Error('No se encontró el foco de ejecución.')
    if (status === 403) throw new Error('No tienes permiso para eliminar este foco.')
    const msg = err?.data?.message
    if (typeof msg === 'string' && msg.trim()) throw new Error(msg)
    if (err instanceof Error && err.message) throw err
    throw new Error('Error al eliminar el foco de ejecución')
  }
}

export const getExecutionFocusVisits = (focusId) => {
  return ClientLambdaApi.get(`/execution-focus/${focusId}/visits`)
    .then(response => {
      if (response.status !== 200) throw new Error('Error al obtener las visitas')
      return response.data
    })
}

export const getExecutionFocusVisit = (visitId) => {
  return ClientLambdaApi.get(`/execution-focus/visit/${visitId}`)
    .then(response => {
      if (response.status !== 200) throw new Error('Error al obtener la visita')
      return response.data
    })
}

export const getExecutionFocusVisitPhoto = (visitId, ruleTypeId) => {
  return ClientLambdaApi.get(`/execution-focus/visit/${visitId}/photo/${ruleTypeId}`)
    .then(response => {
      if (response.status !== 200) throw new Error('Error al obtener la foto')
      return response.data
    })
}



// WEBPACK FOOTER //
// ./src/app/news_feed/Services.js